import { PsHttpService, type PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { MaterialTechQaApi } from '../techqa-api';
import type { ConversationData, CreateChatResponse, FileQuery } from '@/types/tech-qa/tech-qa';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';
import { useGuestMode } from '@/composables/tech-qa/use-guest-mode';

@injectable()
export class MaterialTechQaApiImpl extends PsHttpService implements MaterialTechQaApi {
  @inject(FactoryPsHttpSSE)
  private _factorySsePsHttp!: FactoryPsHttpSSE;

  get options() {
    return {
      baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/material/tech-qa`,
      headers: {
        'X-API-Version': '1.0',
      },
      guestBaseUrl: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/guest/material/tech-qa`,
    };
  }

  getConversation(conversation_id: string): Promise<ConversationData> {
    const { isGuestMode } = useGuestMode();
    const baseURL = isGuestMode.value ? this.options.guestBaseUrl : this.options.baseURL;

    return this.get<PatSnapCommonResponseData<ConversationData>>({
      baseURL,
      url: '/get-conversation',
      params: {
        conversation_id,
      },
    }).then((res) => {
      return res.data as ConversationData;
    });
  }

  createConversation(query: string): Promise<CreateChatResponse> {
    const { isGuestMode } = useGuestMode();
    const baseURL = isGuestMode.value ? this.options.guestBaseUrl : this.options.baseURL;

    return this.post<PatSnapCommonResponseData<CreateChatResponse>>({
      baseURL,
      url: '/chat/create',
      data: {
        query,
      },
    }).then((res) => {
      return res.data as CreateChatResponse;
    });
  }

  getQueryUnderstandingStream(data: {
    query: string;
    conversation_id: string;
    file_query: FileQuery[];
  }) {
    const { isGuestMode } = useGuestMode();
    const baseURL = isGuestMode.value ? this.options.guestBaseUrl : this.options.baseURL;

    return this._factorySsePsHttp<string>()
      .init({
        url: `${baseURL}/chat/query-understanding`,
        data,
        hasFinalMessage: () => true,
      })
      .setFetchHeaders({
        'X-API-Version': '1.0',
      });
  }

  getRetrievalStream(data: Record<any, any>) {
    const { isGuestMode } = useGuestMode();
    const baseURL = isGuestMode.value ? this.options.guestBaseUrl : this.options.baseURL;

    return this._factorySsePsHttp<string>()
      .init({
        url: `${baseURL}/chat/retrieval`,
        data,
        hasFinalMessage: () => true,
      })
      .setFetchHeaders({
        'X-API-Version': '1.0',
      });
  }

  getSummarisationStream(data: Record<any, any>) {
    const { isGuestMode } = useGuestMode();
    const baseURL = isGuestMode.value ? this.options.guestBaseUrl : this.options.baseURL;

    return this._factorySsePsHttp<string>()
      .init({
        url: `${baseURL}/chat/summarisation`,
        data,
        hasFinalMessage: () => true,
      })
      .setFetchHeaders({
        'X-API-Version': '1.0',
      });
  }

  handleShare(conversation_id: string): Promise<Record<any, any>> {
    return this.post<PatSnapCommonResponseData<ConversationData>>({
      url: '/chat/shared',
      data: {
        conversation_id,
      },
    }).then((res) => {
      return res.data;
    });
  }

  shareEmail(conversation_id: string, share_email: string): Promise<Record<any, any>> {
    return this.post<PatSnapCommonResponseData<Record<any, any>>>({
      url: '/chat/share-email',
      data: {
        conversation_id,
        share_email,
      },
    }).then((res) => {
      return res.data;
    });
  }
}
